import React, { useMemo } from "react"
import { useFinanceDataContext } from "../contexts/FinanceDataContext"
import { useTheme } from "../contexts/ThemeContext"

const CalendarGrid = ({
    calendarDays,
    currentMonthItems,
    formatAmount,
    getCategoryColor,
    getCategoryEmoji,
    getProjectedBalance,
    handleDayClick,
    handleTransactionClick,
    user,
    currentMonth,
    prevMonth,
    nextMonth,
    formatDate,
    findMinimumProjectedBalance, 
}) => {
    // Import required hooks and settings from useFinanceData
    const { weatherWarningSettings, selectedScale, balanceScalePresets } = useFinanceDataContext();
    const { theme } = useTheme();

    // Get the minimum balance date and value
    const minBalanceInfo = useMemo(() => {
        return findMinimumProjectedBalance ? findMinimumProjectedBalance() : null;
    }, [findMinimumProjectedBalance]);

    // Helper function to determine if this day is the minimum projected balance day
    const isMinBalanceDay = (dateStr) => {
        return minBalanceInfo && minBalanceInfo.date === dateStr;
    };

    // Helper function to get the appropriate weather forecast for the minimum balance
    const getWeatherWarning = (balance) => {
        if (!weatherWarningSettings) return null;
        
        // Use the stored thresholds from the database if available
        const thresholds = weatherWarningSettings.thresholds || 
            (selectedScale && balanceScalePresets[selectedScale] ? 
                balanceScalePresets[selectedScale].baseThresholds : 
                {
                    stormWarning: 500,
                    rainWarning: 1000,
                    partlyCloudy: 2000
                });

        // Convert balance from cents to dollars for comparison
        const balanceInDollars = balance / 100;

        if (balanceInDollars < thresholds.stormWarning) {
            return weatherWarningSettings.stormWarning;
        } else if (balanceInDollars < thresholds.rainWarning) {
            return weatherWarningSettings.rainWarning;
        } else if (balanceInDollars < thresholds.partlyCloudy) {
            return weatherWarningSettings.partlyCloudy;
        } else {
            return weatherWarningSettings.clearSkies;
        }
    };

    // Helper functions moved from CalendarScreen
    const getTransactionBackground = (transaction) => {
        if (transaction.type === "income") {
            return "#CDF1DE" // Light green for income with lower opacity (20%)
        } else if (
            transaction.type === "expense" &&
            transaction.budgetCategoryId
        ) {
            // Get category color with 20% opacity for background
            const color = getCategoryColor(transaction.budgetCategoryId)
            // Convert hex to RGB and add transparency
            const hexToRgba = (hex, alpha = 0.2) => {
                const r = parseInt(hex.slice(1, 3), 16)
                const g = parseInt(hex.slice(3, 5), 16)
                const b = parseInt(hex.slice(5, 7), 16)
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
            }
            return hexToRgba(color)
        } else {
            return "#f3f4f620" // Default light gray with lower opacity
        }
    }

    // Get transaction border color based on type and category
    const getTransactionBorder = (transaction) => {
        const color = getCategoryColor(transaction.budgetCategoryId)

        if (transaction.isPastDue && !transaction.isPaid) {
            return {
                borderLeftWidth: "3px",
                borderLeftStyle: "dashed",
                borderLeftColor: color,
            }
        } else if (
            transaction.type === "expense" &&
            transaction.budgetCategoryId
        ) {
            // Get category color for border
            return {
                borderLeftWidth: "3px",
                borderLeftStyle: "solid",
                borderLeftColor: color,
            }
        } else if (transaction.type === "income") {
            return {
                borderLeftWidth: "3px",
                borderLeftStyle: "solid",
                borderLeftColor: "#10b981",
            }
        } else {
            return {
                borderLeftWidth: "3px",
                borderLeftStyle: "solid",
                borderLeftColor: "#e5e7eb",
            }
        }
    }

    // Get transaction emoji separately
    const getTransactionEmoji = (transaction) => {
        if (transaction.emoji) {
            return transaction.emoji
        } else if (
            transaction.type === "expense" &&
            transaction.budgetCategoryId
        ) {
            return getCategoryEmoji(transaction.budgetCategoryId)
        } else if (transaction.type === "income") {
            return "💰" // Default emoji for income if none provided
        }
        return "📝" // Default emoji
    }
    
    // Get transaction title separately
    const getTransactionTitle = (transaction) => {
        return transaction.title || transaction.category || transaction.type
    }

    // Original function kept for backward compatibility
    const getTransactionLabel = (transaction) => {
        if (transaction.emoji) {
            return `${transaction.emoji} ${transaction.title || ""}`
        } else if (
            transaction.type === "expense" &&
            transaction.budgetCategoryId
        ) {
            const emoji = getCategoryEmoji(transaction.budgetCategoryId)
            return `${emoji} ${transaction.title || ""}`
        } else if (transaction.type === "income") {
            return `💰 ${transaction.title || ""}`
        }
        return transaction.title || transaction.category || transaction.type
    }

    return (
        <div
            style={{
                position: "relative",
                marginBottom: "80px",
                width: "100%",
                maxWidth: "100%", // Ensure it doesn't exceed viewport width
                overflowX: "hidden", // Prevent horizontal scrolling
            }}
        >
            <div
                style={{
                    backgroundColor: theme.backgroundColor,
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    width: "100%",
                    boxSizing: "border-box", // Include padding in width calculation
                }}
            >
                {/* Day names header */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))", // This is key - minmax prevents overflow
                        gap: "0",
                        borderLeft: `1px solid ${theme.borderColor}`,
                        width: "100%", // Ensure full width
                    }}
                >
                    {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
                        <div
                            key={index}
                            style={{
                                padding: "0.5rem",
                                fontSize: "0.875rem",
                                color: theme.secondaryText,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {day}
                        </div>
                    ))}
                </div>

                {/* Calendar days */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        gap: "0",
                        minHeight: '',
                        borderLeft: `1px solid ${theme.borderColor}`, // Add left border to entire grid
                    }}
                >
                    {calendarDays.map((day, index) => {
                        // Skip rendering hidden days (past dates with no unpaid items)
                        if (day.isHidden) {
                            return (
                                <div
                                    key={index}
                                    style={{
                                        padding: "0.5rem",
                                        minHeight: "6rem",
                                        borderRight: `1px solid ${theme.borderColor}`, // Always show right border
                                        borderBottom: `1px solid ${theme.borderColor}`, // Always show bottom border
                                        backgroundColor: theme.backgroundColor, // Same as visible cells
                                        opacity: 0.3,
                                    }}
                                />
                            )
                        }

                        // Get transactions for this day
                        const dayTransactions = currentMonthItems[day.dateString] || []
                        
                        // Get projected balance for this day ONLY if it's today or has transactions
                        const hasTransactions = dayTransactions.length > 0
                        const isToday = day.isToday
                        
                        // Only get the projected balance if it's today or has transactions
                        const projectedBalance = (isToday || hasTransactions) 
                            ? getProjectedBalance(day.dateString)
                            : null

                        // Check if this is the minimum balance day
                        const isMinBalance = isMinBalanceDay(day.dateString);
                        
                        // Get the weather warning if this is the min balance day
                        const weatherWarning = isMinBalance && minBalanceInfo 
                            ? getWeatherWarning(minBalanceInfo.balance) 
                            : null;

                        return (
                            <div
                                key={index}
                                style={{
                                    padding: "0.25rem", // Reduce padding on mobile
                                    borderRight: `1px solid ${theme.borderColor}`,
                                    borderBottom: `1px solid ${theme.borderColor}`,
                                    backgroundColor: theme.backgroundColor,
                                    opacity: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    cursor: user ? "pointer" : "default",
                                    minWidth: "0", // Important - allows cell to shrink below content size
                                    minHeight: "10rem", // Set your desired minimum height here
                                    overflow: "hidden", // Prevent content from breaking layout
                                }}
                                onClick={() => user && handleDayClick(day)}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center", // Center horizontally
                                        alignItems: "center",
                                        marginBottom: "0.5rem", // Slightly increased margin
                                        position: "relative",
                                    }}
                                >
                                    {/* Show month first for the 1st day of month */}
                                    {day.date.getDate() === 1 ? (
                                        <>
                                            <span
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "0.875rem",
                                                    marginRight: "0.25rem",
                                                }}
                                            >
                                                {day.date.toLocaleDateString(
                                                    "en-US",
                                                    {month: "short"}
                                                )}
                                            </span>
                                            <span
                                                style={{
                                                    fontWeight: day.isToday
                                                        ? "bold"
                                                        : "bold",
                                                    width: "1.75rem", // Consistent width for all date numbers
                                                    height: "1.75rem", // Consistent height
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    borderRadius: day.isToday
                                                        ? "50%"
                                                        : "0", // Circle only for today
                                                    backgroundColor: day.isToday
                                                        ? "#3b82f6"
                                                        : "transparent",
                                                    color: day.isToday
                                                        ? "white"
                                                        : day.isPrevMonth ||
                                                          day.isNextMonth
                                                        ? "#9ca3af"
                                                        : day.date <
                                                          new Date().setHours(
                                                              0,
                                                              0,
                                                              0,
                                                              0
                                                          )
                                                        ? "#9ca3af"
                                                        : "#000",
                                                    fontSize: "0.875rem", // Consistent font size
                                                }}
                                            >
                                                {day.date.getDate()}
                                            </span>
                                        </>
                                    ) : (
                                        <span
                                            style={{
                                                fontWeight: day.isToday
                                                    ? "bold"
                                                    : "normal",
                                                width: "1.75rem", // Consistent width for all date numbers
                                                height: "1.75rem", // Consistent height
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderRadius: day.isToday
                                                    ? "50%"
                                                    : "0", // Circle only for today
                                                backgroundColor: day.isToday
                                                    ? "#3b82f6"
                                                    : "transparent",
                                                color: day.isToday
                                                    ? "white"
                                                    : day.isPrevMonth ||
                                                      day.isNextMonth
                                                    ? "#9ca3af"
                                                    : day.date <
                                                      new Date().setHours(
                                                          0,
                                                          0,
                                                          0,
                                                          0
                                                      )
                                                    ? "#9ca3af"
                                                    : "#000",
                                                fontSize: "0.875rem", // Consistent font size
                                            }}
                                        >
                                            {day.date.getDate()}
                                        </span>
                                    )}
                                </div>
                                {/* Projected balance display - ONLY show for today or days with transactions or min balance day */}
                                {(projectedBalance !== null || isMinBalance) && (isToday || hasTransactions || isMinBalance) && (
                                    <div
                                        style={{
                                            backgroundColor: isMinBalance && weatherWarning 
                                                ? `${weatherWarning.color}20` // Use 20% opacity for the weather color
                                                : "#f3f4f6",
                                            padding: "0.25rem 0.5rem",
                                            borderRadius: "0.90rem",
                                            marginBottom: "0.5rem",
                                            alignSelf: "center", // Center horizontally
                                            fontSize: "0.85rem",
                                            fontWeight: "600",
                                            color: isMinBalance && weatherWarning 
                                                ? weatherWarning.color // Use the weather color for text
                                                : "black",
                                            opacity: user ? 1 : 0.7,
                                            textAlign: "center", // Center text within the element
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "4px",
                                        }}
                                    >
                                        {isMinBalance && weatherWarning && (
                                            <span>{weatherWarning.emoji}</span>
                                        )}
                                        {formatAmount(isMinBalance && minBalanceInfo ? (minBalanceInfo.balance / 100) : projectedBalance, {
                                            showSign: (isMinBalance && minBalanceInfo ? (minBalanceInfo.balance / 100) : projectedBalance) < 0,
                                        })}
                                    </div>
                                )}

                                {/* Map through actual transactions for this day */}
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "0.25rem",
                                    }}
                                >
                                    {dayTransactions.map(
                                        (transaction, idx) => {
                                            const backgroundColor =
                                                getTransactionBackground(
                                                    transaction
                                                )
                                            const borderStyle =
                                                getTransactionBorder(
                                                    transaction
                                                )
                                            const emoji =
                                                getTransactionEmoji(
                                                    transaction
                                                )
                                            const title =
                                                getTransactionTitle(
                                                    transaction
                                                )
                                            const isPaid =
                                                transaction.isPaid
                                          
                                            const label =
                                                getTransactionLabel(
                                                    transaction
                                                ) 
                                            return (
                                                <div
                                                    key={`${transaction.id}-${idx}`}
                                                    style={{
                                                        backgroundColor:
                                                            backgroundColor,
                                                        padding: "0.25rem",
                                                        minHeight: "2rem",
                                                        borderRadius:
                                                            "0.55rem",
                                                        color: "inherit", 
                                                        ...borderStyle,
                                                        cursor: "pointer",
                                                        boxShadow:
                                                            "0 1px 2px rgba(0,0,0,0.05)",
                                                        opacity: isPaid
                                                            ? 0.5
                                                            : 1,
                                                        textDecoration:
                                                            isPaid
                                                                ? "line-through"
                                                                : "none",
                                                    }}
                                                    title={`${label} - ${formatAmount(
                                                        transaction.type === "expense" ? -Math.abs(transaction.amount) : transaction.amount,
                                                        {showSign: true}
                                                    )}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleTransactionClick(
                                                            transaction
                                                        )
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                            alignItems:
                                                                "flex-start",
                                                            width: "100%",
                                                            textAlign:
                                                                "left",
                                                            minWidth: "0", 
                                                            overflow:
                                                                "hidden", 
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    "1.25rem", 
                                                                marginBottom:
                                                                    "2px",
                                                            }}
                                                        >
                                                            {emoji}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    window.innerWidth <
                                                                    640
                                                                        ? "0.7rem"
                                                                        : "0.8rem", 
                                                                fontWeight:
                                                                    "medium",
                                                                marginBottom:
                                                                    "2px",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                width: "100%",
                                                            }}
                                                        >
                                                            {title}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize:
                                                                    "0.8rem", 
                                                                fontWeight:
                                                                    "bold",
                                                                color: theme.textColor, 
                                                            }}
                                                        >
                                                            {formatAmount(
                                                                transaction.type === "expense" ? -Math.abs(transaction.amount) : transaction.amount,
                                                                {
                                                                    showSign: true,
                                                                }
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    )}
                                    {day.isToday &&
                                        dayTransactions.length === 0 && (
                                            <div
                                                style={{
                                                    fontSize: "0.75rem",
                                                    color: theme.tertiaryText,
                                                    fontStyle: "italic",
                                                    padding:
                                                        "0.25rem 0.5rem",
                                                    textAlign: "center",
                                                }}
                                            >
                                                No plans today!
                                            </div>
                                        )}
                                </div>

                                {/* Display a placeholder message for unauthenticated users */}
                                {!user && (
                                    <div
                                        style={{
                                            fontSize: "0.75rem",
                                            color: theme.tertiaryText,
                                            fontStyle: "italic",
                                            padding: "0.25rem 0.5rem",
                                            textAlign: "center",
                                            marginTop: "1rem",
                                        }}
                                    >
                                        {day.isToday
                                            ? "Today's transactions will appear here"
                                            : ""}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* New navigation bar at the bottom of the screen */}
            <div
                style={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 20,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 10,
                }}
            >
                {user && <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        maxWidth: "240px",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <button
                        onClick={prevMonth}
                        style={{
                            height: "50px",
                            backgroundColor: theme.backgroundColor,
                            border: "none",
                            borderRadius: 50,
                            paddingLeft: 12,
                            paddingRight: 12,
                            cursor: "pointer",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                        }}
                        aria-label='Previous month'
                    >
                        ↑ Previous
                    </button>

                    <button
                        onClick={nextMonth}
                        style={{
                            height: "50px",
                            backgroundColor: theme.backgroundColor,
                            border: "none",
                            borderRadius: 50,
                            paddingLeft: 20,
                            paddingRight: 20,
                            cursor: "pointer",
                            fontSize: "1.05rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                        }}
                        aria-label='Next month'
                    >
                        Next ↓
                    </button>
                </div>}
                
            </div>
        </div>
    )
}

export default CalendarGrid